import type { Transaction } from '@tiptap/pm/state';
import { ensureImageDimensions } from './image-dimensions';

export class ImageView {
  props: any;
  doc: Document;
  dom: HTMLElement;
  img!: HTMLImageElement;
  caption!: HTMLElement;
  wrapper!: HTMLDivElement;

  constructor(props: any, doc: Document, dom: HTMLElement) {
    this.props = props;
    this.doc = doc;
    this.dom = dom;

    this.initStructure();
    this.render();
  }

  private stopBubbling = (e: Event) => e.stopPropagation();

  destroy() {
    // 1. Clean up caption listeners
    const events = ["mousedown", "keydown", "keyup", "keypress", "input", "paste"];
    events.forEach(event => this.caption.removeEventListener(event, this.stopBubbling));
    
    // 2. Clear global side-effects just in case
    this.doc.body.style.cursor = "";
    this.props.editor.view.dom.style.pointerEvents = "auto";

    // 3. Nullify references to help GC
    this.caption.onfocus = null;
    this.caption.onblur = null;
    this.caption.onclick = null;
    this.img.onload = null;
  }

  initStructure() {
    const container = this.doc.createElement("div");
    container.className = "image-container";

    this.wrapper = this.doc.createElement("div");
    this.wrapper.className = "image-wrapper";
    this.wrapper.style.position = "relative";
    this.wrapper.style.display = "inline-block";

    this.img = this.doc.createElement("img");
    this.img.style.display = "block";
    this.img.onload = () => ensureImageDimensions(this.props, this.img);

    // "as any" to bypass figcaption lint error
    this.caption = this.doc.createElement("figcaption") as any;
    this.caption.className = "image-caption";
    this.caption.setAttribute("contenteditable", "true");

    // Prevent ProseMirror from seeing typing/clicks and replacing the node
    this.caption.addEventListener("mousedown", this.stopBubbling);
    this.caption.addEventListener("keydown", this.stopBubbling);
    this.caption.addEventListener("keyup", this.stopBubbling);
    this.caption.addEventListener("keypress", this.stopBubbling);
    this.caption.addEventListener("input", this.stopBubbling);
    this.caption.addEventListener("paste", this.stopBubbling);

    this.caption.onclick = (e) => {
      e.stopPropagation();
      this.caption.focus();
    };

    this.caption.onfocus = () => {
      // Disable dragging so text selection works inside the caption
      this.props.editor.setOptions({ editable: true }); 
      this.dom.setAttribute("draggable", "false");
    };

    this.caption.onblur = () => {
      this.dom.setAttribute("draggable", "true");
      const value = this.caption.innerText.trim();
      const { editor, getPos } = this.props;
      if (editor.isDestroyed) return;
      const pos = getPos();
      if (typeof pos !== "number") return;
      editor.commands.command(({ tr }: { tr: Transaction }) => {
        const node = tr.doc.nodeAt(pos);
        if (!node || node.type !== this.props.node.type || node.attrs.caption === value) return false;
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, caption: value });
        return true;
      });
    };

    this.wrapper.appendChild(this.img);
    container.appendChild(this.wrapper);
    container.appendChild(this.caption);
    this.dom.appendChild(container);
  }

  render() {
    const { node, selected } = this.props;
    const { src, alt, title, width, height, align, caption } = node.attrs;

    if (this.img.getAttribute("src") !== (src || "")) this.img.src = src || "";
    this.img.alt = alt || "";
    this.img.title = title || "";

    // The figure and its editor-only containers must fit as well as the image.
    // Clear these constraints on Undo so the original layout is restored.
    const responsive = !!node.attrs.responsive;
    this.dom.style.width = responsive ? '100%' : '';
    this.dom.style.maxWidth = responsive ? '100%' : '';
    this.dom.style.minWidth = responsive ? '0' : '';
    this.dom.style.boxSizing = responsive ? 'border-box' : '';
    this.dom.style.marginLeft = responsive ? '0' : '';
    this.dom.style.marginRight = responsive ? '0' : '';
    this.dom.style.overflowWrap = responsive ? 'anywhere' : '';
    const container = this.wrapper.parentElement!;
    container.style.maxWidth = responsive ? '100%' : '';
    container.style.minWidth = responsive ? '0' : '';


    
    // Only update styles if we aren't mid-resize
    if (this.doc.body.style.cursor === "") {
      this.img.style.width = width ? `${width}px` : "auto";
      this.img.style.height = node.attrs.responsive ? 'auto' : height ? `${height}px` : "auto";
      this.img.style.maxWidth = node.attrs.responsive ? '100%' : '';
    }

    const alignmentClass = `align-${align || 'center'}`;
    const selectionClass = selected ? "ProseMirror-selectednode" : "";
    this.dom.className = `image-node image-block ${alignmentClass} ${selectionClass}`;

    this.img.style.border = selected ? "2px solid #2684FF" : "none";

    this.dom.className = `image-node image-block align-${align} ${
      selected ? "ProseMirror-selectednode" : ""
    }`;

    // Update text content only if not currently focused to avoid cursor reset
    if (this.doc.activeElement !== this.caption) {
      this.caption.innerText = caption || "";
    }

    this.renderToolbar(selected);
    this.renderResizeHandles(selected);
  }

  renderToolbar(selected: boolean) {
    this.dom.querySelector(".image-toolbar")?.remove();
    if (!selected) return;

    const toolbar = this.doc.createElement("div");
    toolbar.className = "image-toolbar";
    toolbar.setAttribute("role", "toolbar");
    toolbar.setAttribute("aria-label", "Image controls");
    toolbar.onmousedown = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    ["left", "center", "right"].forEach((dir) => {
      const btn = this.doc.createElement("button");
      btn.type = "button";
      btn.innerText = dir.charAt(0).toUpperCase() + dir.slice(1);
      btn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.editor.chain().focus().updateAttributes("image", { align: dir }).run();
        //this.props.editor.commands.updateAttributes("image", { align: dir });
      };
      toolbar.appendChild(btn);
    });

    const delBtn = this.doc.createElement("button");
    delBtn.type = "button";
    delBtn.innerText = "Delete";
    delBtn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.props.editor.chain().focus().deleteSelection().run();
    };
    toolbar.appendChild(delBtn);

    // Keep controls outside the image's clipped wrapper, with their own layout space.
    this.dom.prepend(toolbar);
  }

  renderResizeHandles(selected: boolean) {
    this.wrapper.querySelectorAll(".image-resize-handle").forEach((h) => h.remove());
    if (!selected) return;

    const handleRight = this.doc.createElement("div");
    handleRight.className = "image-resize-handle image-resize-handle-right";
    handleRight.onmousedown = (e: any) => this.startResize(e, "right");

    const handleCorner = this.doc.createElement("div");
    handleCorner.className = "image-resize-handle image-resize-handle-corner";
    handleCorner.onmousedown = (e: any) => this.startResize(e, "corner");

    this.wrapper.appendChild(handleRight);
    this.wrapper.appendChild(handleCorner);
  }

  startResize(event: PointerEvent | MouseEvent, direction: "right" | "corner") {
    event.preventDefault();
    event.stopPropagation();

    const handle = event.currentTarget as HTMLElement;
    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = this.img.offsetWidth;
    const startHeight = this.img.offsetHeight;
    const aspectRatio = startWidth / startHeight;

    try {
        if (handle.setPointerCapture) {
            handle.setPointerCapture((event as any).pointerId || 1);
        }
    } catch (e) {}

    this.doc.body.style.cursor = direction === "right" ? "ew-resize" : "nwse-resize";
    // Block editor pointer events so it doesn't try to select text during drag
    this.props.editor.view.dom.style.pointerEvents = "none";

    const onPointerMove = (e: PointerEvent | MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;

      if (direction === "right") {
        newWidth = Math.max(50, startWidth + deltaX);
      } else {
        const delta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
        newWidth = Math.max(50, startWidth + delta);
        newHeight = newWidth / aspectRatio;
      }

      this.img.style.width = `${Math.round(newWidth)}px`;
      this.img.style.height = `${Math.round(newHeight)}px`;
    };

    const onPointerUp = (e: PointerEvent | MouseEvent) => {
      handle.removeEventListener("pointermove", onPointerMove as any);
      handle.removeEventListener("pointerup", onPointerUp as any);
      handle.removeEventListener("mousemove", onPointerMove as any);
      handle.removeEventListener("mouseup", onPointerUp as any);

      try {
        if (handle.releasePointerCapture) {
            handle.releasePointerCapture((event as any).pointerId || 1);
        }
      } catch (e) {}

      this.doc.body.style.cursor = "";
      this.props.editor.view.dom.style.pointerEvents = "auto";

      this.props.editor.commands.updateAttributes("image", {
        width: parseInt(this.img.style.width, 10),
        height: parseInt(this.img.style.height, 10),
      });

      this.props.editor.commands.focus();
    };

    handle.addEventListener("pointermove", onPointerMove as any);
    handle.addEventListener("pointerup", onPointerUp as any);
    handle.addEventListener("mousemove", onPointerMove as any);
    handle.addEventListener("mouseup", onPointerUp as any);
  }

  selectNode() {
    this.props.selected = true;
    this.render();
  }

  deselectNode() {
    this.props.selected = false;
    this.render();
  }

  update(node: any) {
    if (node.type !== this.props.node.type) return false;
    this.props.node = node;
    this.render();
    return true;
  }
}
